import type { Writable } from "svelte/store";
import * as filepath from "./filepath";
import { FS } from "./fs";
import type { PcloudConfig } from "./fs";
import { FsDir } from "./fsentry";
import { xhrAsync, XhrOpts } from "./http";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://docs.pcloud.com/
// The pCloud API answers every origin with Access-Control-Allow-Origin: *, so
// the browser calls it directly and the password never reaches our server.
// It does not allow custom request headers, so the login token travels as the
// auth query parameter and uploads are multipart form posts.

// Accounts live in one of two regions. Login tries them in this order.
export const pcloudHosts = ["api.pcloud.com", "eapi.pcloud.com"];

// pCloud's own code for a wrong email or password
const errLoginFailed = 2000;

export const prop_pcloud_content_type = "contenttype";
export const prop_pcloud_hash = "hash";

interface PcloudMeta {
  id?: string;
  name?: string;
  isfolder?: boolean;
  size?: number;
  // seconds since epoch, because calls ask for timeformat=timestamp
  modified?: number;
  contenttype?: string;
  hash?: number | string;
}

class PcloudError extends Error {
  result: number;
  constructor(result: number, message: string) {
    super(message);
    this.result = result;
  }
}

class FileSystemPcloud extends FS {
  supportsDirRename = true;

  constructor(config: PcloudConfig) {
    super(config);
  }

  get pcloudConfig(): PcloudConfig {
    return this.config as PcloudConfig;
  }

  call(method: string, params: Record<string, string>): Promise<any> {
    let c = this.pcloudConfig;
    return pcloudCall(c.host, method, { auth: c.auth, ...params });
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await this.call("listfolder", {
        path: filepath.stripDirSlash(dirPath),
        timeformat: "timestamp",
      });
      let contents = (js.metadata?.contents || []) as PcloudMeta[];
      return FsDir.fromWire(dirPath, pcloudContentsToWire(contents));
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let js = await this.call("getfilelink", { path: path });
    let hosts = (js.hosts || []) as string[];
    throwIf(hosts.length == 0 || !js.path, "pCloud did not return a download url");
    return `https://${hosts[0]}${js.path}`;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let c = this.pcloudConfig;
    let params: Record<string, string> = {
      auth: c.auth,
      path: filepath.dir(path) || "/",
      // without this a failed upload leaves a partial file
      nopartial: "1",
    };
    if (file instanceof File) {
      params.mtime = String(Math.floor(file.lastModified / 1000));
    }
    let form = new FormData();
    form.append("file", file, filepath.base(path));
    let opts = new XhrOpts();
    opts.method = "POST";
    opts.params = form;
    opts.httpProgress = httpProgress;
    let xhr: XMLHttpRequest;
    try {
      xhr = await xhrAsync(pcloudURL(c.host, "uploadfile", params), opts);
    } catch (e: any) {
      let status = e && e.status ? ` ${e.status}` : "";
      throw new Error(`pCloud upload failed${status}`);
    }
    pcloudCheck(parseJSON(xhr.responseText));
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    path = filepath.stripDirSlash(path);
    // A topath that ends in a slash would move the entry into that folder.
    newPath = filepath.stripDirSlash(newPath);
    let found = this.findEntries(path);
    let isDir = found.dir.entryIsDir(found.entries[0]);
    await this.call(isDir ? "renamefolder" : "renamefile", { path: path, topath: newPath });
  }

  async createDirectory(dirPath: string): Promise<void> {
    dirPath = filepath.stripDirSlash(dirPath);
    throwIf(dirPath == "/", "missing directory name");
    await this.call("createfolderifnotexists", { path: dirPath });
  }

  async deleteFile(path: string): Promise<void> {
    if (!path.endsWith("/")) {
      await this.call("deletefile", { path: path });
      return;
    }
    path = filepath.stripDirSlash(path);
    throwIf(path == "/", "cannot delete the root folder");
    await this.call("deletefolderrecursive", { path: path });
  }
}

export function pcloudContentsToWire(contents: PcloudMeta[]): any[][] {
  let wires: any[][] = [];
  for (let m of contents) {
    if (!m.name) {
      continue;
    }
    let isDir = !!m.isfolder;
    let size = isDir ? -1 : m.size || 0;
    let lastMod = isDir ? 0 : (m.modified || 0) * 1000;
    let meta: any[] = [m.id || "", m.name, size, lastMod, isDir ? [] : null];
    if (!isDir && m.contenttype) {
      meta.push(prop_pcloud_content_type, m.contenttype);
    }
    if (!isDir && m.hash) {
      meta.push(prop_pcloud_hash, String(m.hash));
    }
    wires.push(meta);
  }
  return wires;
}

async function sha1Hex(s: string): Promise<string> {
  let d = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(s));
  let hex = "";
  for (let b of new Uint8Array(d)) {
    hex += b.toString(16).padStart(2, "0");
  }
  return hex;
}

// The login proof pCloud documents for getdigest: the password itself is not sent.
export async function pcloudPasswordDigest(email: string, password: string, digest: string): Promise<string> {
  let emailHash = await sha1Hex(email.toLowerCase());
  return sha1Hex(password + emailHash + digest);
}

/**
 * Log in with an email and password. Returns the login token and the region
 * host that accepted it.
 */
export async function pcloudLogin(email: string, password: string): Promise<{ auth: string; host: string }> {
  let firstErr: Error | undefined;
  for (let host of pcloudHosts) {
    try {
      let js = await pcloudCall(host, "getdigest", {});
      let digest = js.digest as string;
      throwIf(!digest, "pCloud did not return a login digest");
      js = await pcloudCall(host, "userinfo", {
        getauth: "1",
        logout: "1",
        username: email,
        digest: digest,
        passworddigest: await pcloudPasswordDigest(email, password, digest),
        // the token stops working after this many seconds without use: the most pCloud allows
        authinactiveexpire: "5356800",
      });
      let auth = js.auth as string;
      throwIf(!auth, "pCloud did not return a login token");
      return { auth, host };
    } catch (e) {
      firstErr = firstErr || (e as Error);
      // A wrong password and an account in the other region look the same.
      if (!(e instanceof PcloudError) || e.result != errLoginFailed) {
        break;
      }
    }
  }
  throw firstErr;
}

function pcloudURL(host: string, method: string, params: Record<string, string>): string {
  return `https://${host}/${method}?${new URLSearchParams(params).toString()}`;
}

function parseJSON(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

// pCloud answers 200 even for errors. result is 0 on success.
function pcloudCheck(js: any): any {
  if (js.result !== 0) {
    let result = typeof js.result == "number" ? js.result : -1;
    throw new PcloudError(result, js.error ? `pCloud: ${js.error}` : "pCloud request failed");
  }
  return js;
}

async function pcloudCall(host: string, method: string, params: Record<string, string>): Promise<any> {
  let rsp: Response;
  try {
    rsp = await fetch(pcloudURL(host, method, params));
  } catch {
    // the default message would quote the url, which carries the login token
    throw new Error("could not connect to pCloud");
  }
  return pcloudCheck(parseJSON(await rsp.text()));
}

export function makeFileSystemPcloud(c: PcloudConfig): FileSystemPcloud {
  return new FileSystemPcloud(c);
}
